import { useState } from 'react';
import { Sheet, SheetContent } from '@/shared/widgets/sheet';
import { PLP_SORT_OPTIONS, type PlpSortByValue } from '../../constants';

interface PlpSortSheetProps {
  open: boolean;
  currentSortBy: PlpSortByValue;
  onOpenChange: (open: boolean) => void;
  onApply: (nextSortBy: PlpSortByValue) => void;
  /** Analytics: fires when user taps a sort radio (draft selection, not yet applied) */
  onSortOptionSelect?: (value: PlpSortByValue) => void;
  /** Analytics: fires when user taps the Cancel button */
  onCancel?: () => void;
}

export function PlpSortSheet({
  open,
  currentSortBy,
  onOpenChange,
  onApply,
  onSortOptionSelect,
  onCancel,
}: PlpSortSheetProps) {
  const [draftSortBy, setDraftSortBy] = useState<PlpSortByValue>(currentSortBy);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) setDraftSortBy(currentSortBy);
    onOpenChange(nextOpen);
  };

  const handleApply = () => {
    onApply(draftSortBy);
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="bottom"
        className="h-auto max-h-[80vh] overflow-hidden p-0"
        showCloseButton={false}
      >
        <div className="bg-white">
          <div className="px-6 py-5">
            <h2 className="border-b border-gray-200 pb-3 text-[20px] font-semibold text-gray-900">
              Sort By
            </h2>
            <div className="pt-2">
              {PLP_SORT_OPTIONS.map((option) => {
                const selected = draftSortBy === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    className="flex w-full items-center justify-between py-3 text-left"
                    onClick={() => {
                      onSortOptionSelect?.(option.value);
                      setDraftSortBy(option.value);
                    }}
                  >
                    <span className="text-[14px] text-gray-900">{option.label}</span>
                    <span
                      className={[
                        'h-7 w-7 rounded-full border-2',
                        selected
                          ? 'border-[#3F84AD] bg-[#3F84AD]'
                          : 'border-[#3F84AD] bg-transparent',
                      ].join(' ')}
                      aria-hidden
                    />
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex border-t border-gray-200">
            <button
              type="button"
              className="flex-1 py-4 text-[14px] font-medium text-gray-500"
              aria-label="Cancel sort selection"
              onClick={() => { onCancel?.(); onOpenChange(false); }}
            >
              Cancel
            </button>
            <span className="w-px bg-gray-200" aria-hidden />
            <button
              type="button"
              className="flex-1 py-4 text-[14px] font-medium text-[#3F84AD]"
              aria-label="Apply sort selection"
              onClick={handleApply}
            >
              Apply
            </button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

